import { Component, Injector, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, FormBuilder, FormArray } from '@angular/forms';
import { DynamicService } from 'src/app/services/dynamic.service';
import { MasterService } from 'src/app/services/master.service';
import { MessageService } from 'src/app/services/message.service';
import { token } from '../roles.component';
import * as languageLibrary from '../../../../../../services/language'
import { formatCurrency, formatPercent } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { DcaTermRange } from 'src/app/models/dca-term-range.model';

@Component({
    selector: '[role-dca]',
    templateUrl: './role-dca.component.html',
    styleUrls: ['./role-dca.component.scss'],
    standalone: false
})
export class RoleDCAComponent implements OnInit {
    /** */
    protected contactId: number

    /** */
    protected contactRoleId: number = 0
    
    /** */
    protected words = languageLibrary.language
    
    /** */
    protected language: string = 'EN'
    
    /** */
    public dcaInformation: FormGroup;
    
    /** */
    protected dataIn: string;
    
    /** */
    public id: number = 0;

    /** */
    public calledFrom: string;
    
    /**
     * Helper method to consistently get the list of term ranges as an Array.
     */
    get terms() {
        return this.dcaInformation.controls['terms'] as FormArray;
    }

    constructor(private master: MasterService, private ms: MessageService, private inject: Injector, private serv: DynamicService, private fb: FormBuilder) {
        this.dcaInformation = this.fb.group({
            id: new FormControl<number>(null),
            contactId: new FormControl<number>(null, [Validators.required]),
            costPercentOfFee: new FormControl<string>(formatPercent(0)),
            feeCapPercentage: new FormControl<string>(formatPercent(0)),
            terms: this.fb.array([])
        })
    }

    ngOnInit(): void {

        this.dataIn = this.inject.get(token);
        this.contactId = this.dataIn['contactId']
        this.contactRoleId = this.dataIn['contactRoleId']
        this.calledFrom = this.dataIn['calledFrom']

        if (!this.contactId) {
            return
        }

        this.dcaInformation.get("contactId").setValue(this.contactId)

        this.getDCA()
    }

    deactivateRole() {
        this.serv.outputFromDynamicComponent({ action: "deactivate", contactRoleId: this.contactRoleId });
    }

    public getDCA() {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        this.master.get(`contacts/${this.contactId}/dca`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (res.data.contactDCA.length == 0) {
                if (this.calledFrom == "contactAdvancedSearch")
                    this.serv.outputFromDynamicComponent({ action: "setupNeeded", setupNeeded: true, type: "debtCancellation" });
                return
            }

            this.setInformation(res.data.contactDCA[0])

            if (this.calledFrom == "contactAdvancedSearch") {
                this.serv.outputFromDynamicComponent({ action: "setupNeeded", setupNeeded: false, type: "debtCancellation" });
                return
            }
        })
    }

    public saveDCA() {
        if (!this.dcaInformation.valid) {
            return
        }

        const id = parseInt(this.dcaInformation.get('id').value)
        if (!id || isNaN(id) || (id <= 0)) {
            this.createDCA()
        }

        this.modifyDCA()
    }

    createDCA() {
        const contactId = parseInt(this.dcaInformation.get('contactId').value)
        if (!contactId || isNaN(contactId) || (contactId <= 0)) {
            return
        }

        const payload = {
            costPercentOfFee: clean(this.dcaInformation.get('costPercentOfFee').value),
            feeCapPercentage: clean(this.dcaInformation.get('feeCapPercentage').value),
            terms: null
        }

        for (const term of this.terms.controls) {
            if (!payload.terms) {
                payload.terms = []
            }

            payload.terms.push({
                termMonthsFrom: term.get('termMonthsFrom').value,
                termMonthsTo: term.get('termMonthsFrom').value,
                dollarCap: clean(term.get('dollarCap').value)
            })
        }

        this.master.post(`contacts/${contactId}/dca`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
            this.setInformation(res.data.contactDCA)

            if (this.calledFrom == "contactAdvancedSearch")
                this.serv.outputFromDynamicComponent({ action: "setupSaved", setupSaved: true, type: "debtCancellation" });
        })
    }

    modifyDCA() {
        const id = parseInt(this.dcaInformation.get('id').value)
        if (!id || isNaN(id) || (id <= 0)) {
            return
        }

        const contactId = parseInt(this.dcaInformation.get('contactId').value)
        if (!contactId || isNaN(contactId) || (contactId <= 0)) {
            return
        }

        const payload = {
            costPercentOfFee: clean(this.dcaInformation.get('costPercentOfFee').value),
            feeCapPercentage: clean(this.dcaInformation.get('feeCapPercentage').value),
            terms: null
        }

        for (const term of this.terms.controls) {
            if (!payload.terms) {
                payload.terms = []
            }
            
            payload.terms.push({
                termMonthsFrom: term.get('termMonthsFrom').value,
                termMonthsTo: term.get('termMonthsFrom').value,
                dollarCap: clean(term.get('dollarCap').value)
            })
        }

        this.master.put(`contacts/${contactId}/dca/${id}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (this.calledFrom == "contactAdvancedSearch") {
                this.serv.outputFromDynamicComponent({ action: "setupSaved", setupSaved: true, type: "debtCancellation" })
            }
        })
    }

    deleteDCA() {
        const id = parseInt(this.dcaInformation.get('id').value)
        if (!id || isNaN(id) || (id <= 0)) {
            return
        }

        const contactId = parseInt(this.dcaInformation.get('contactId').value)
        if (!contactId || isNaN(contactId) || (contactId <= 0)) {
            return
        }

        this.master.discard(`contacts/${this.contactId}/dca/${this.id}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.resetInformation()
        })
    }

    resetInformation() {
        this.dcaInformation.reset({
            contactId: this.contactId
        })

        this.id = null
    }

    setInformation(info) {
        this.id = info["id"]

        this.dcaInformation.setValue({
            id: info['id'],
            contactId: info['contactId'],
            costPercentOfFee: formatPercent(info['costPercentOfFee']),
            feeCapPercentage: formatPercent(info['feeCapPercentage']),
            terms: []
        })

        const ranges: DcaTermRange[] = JSON.parse(info['termRange'])
        if (!ranges || (!Array.isArray(ranges)) || (ranges.length <= 0)) {
            return
        }

        for (const range of ranges) {
            this.terms.push(this.fb.group({
                termMonthsFrom: new FormControl<number>(range.termMonthsFrom, []),
                termMonthsTo: new FormControl<number>(range.termMonthsTo, []),
                dollarCap: new FormControl<string>(formatCurrency(range.dollarCap), [])
            }))
        }
    }

     /**
     * Add a new item to the gap terms ranges form array.
     */
    addTerms = () => {
        const term = this.fb.group({
            termMonthsFrom: [0, []],
            termMonthsTo: [0, []],
            dollarCap: [formatCurrency(0), []]
        })

        this.terms.push(term)
    }

    /**
     * Remove one of the gap terms ranges idenitified by the given index.
     * @param index Index of the gap terms range to remove
     */
    discardTerms = (index: number) => {
        if ((index < 0) || (index > (this.terms.length-1))) {
            return
        }

        this.terms.removeAt(index)
    }

}
